// src/firebase/stats.js
// Marker Statistics Service - tracks how many homeworks each marker has marked

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  increment,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from './config';
import { getCurrentUserOrgId } from './orgs';

// Every homework count in a stats doc. Counts are keyed by homework type
// code, and other tutors use their own codes (not just TH1-RH4), so take
// every number in the doc rather than a fixed list.
const LEGACY_TYPES = ['TH1', 'TH2', 'TH3', 'TH4', 'RH1', 'RH2', 'RH3', 'RH4'];
const homeworkCounts = (stats) => {
  const counts = {};
  LEGACY_TYPES.forEach(type => { counts[type] = 0; });
  Object.entries(stats || {}).forEach(([key, value]) => {
    if (typeof value === 'number') counts[key] = value;
  });
  return counts;
};
const zeroCounts = (stats) => {
  const zeros = {};
  Object.keys(homeworkCounts(stats)).forEach(type => { zeros[type] = 0; });
  return zeros;
};

/**
 * Get statistics for a specific marker (marker's resettable view)
 */
export const getMarkerStats = async (markerId) => {
  try {
    const docRef = doc(db, 'markerStats', markerId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      // Return zero stats if none exist
      return {
        TH1: 0,
        TH2: 0,
        TH3: 0,
        TH4: 0,
        RH1: 0,
        RH2: 0,
        RH3: 0,
        RH4: 0,
        lastReset: null
      };
    }
    
    return docSnap.data();
  } catch (error) {
    console.error('Get marker stats error:', error);
    return {
      TH1: 0,
      TH2: 0,
      TH3: 0,
      TH4: 0,
      RH1: 0,
      RH2: 0,
      RH3: 0,
      RH4: 0,
      lastReset: null
    };
  }
};

/**
 * Get statistics for all markers in the caller's own org
 */
export const getAllMarkerStats = async (orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const q = query(collection(db, 'markerStats'), where('orgId', '==', resolvedOrgId));
    const querySnapshot = await getDocs(q);
    const stats = {};

    querySnapshot.forEach((doc) => {
      stats[doc.id] = doc.data();
    });

    return stats;
  } catch (error) {
    console.error('Get all marker stats error:', error);
    return {};
  }
};

/**
 * Get head marker's independent view of marker stats (not affected by marker resets)
 */
export const getHeadMarkerViewStats = async (markerId) => {
  try {
    const docRef = doc(db, 'headMarkerStats', markerId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return {
        TH1: 0,
        TH2: 0,
        TH3: 0,
        TH4: 0,
        RH1: 0,
        RH2: 0,
        RH3: 0,
        RH4: 0,
        lastReset: null
      };
    }
    
    return docSnap.data();
  } catch (error) {
    console.error('Get head marker view stats error:', error);
    return {
      TH1: 0,
      TH2: 0,
      TH3: 0,
      TH4: 0,
      RH1: 0,
      RH2: 0,
      RH3: 0,
      RH4: 0,
      lastReset: null
    };
  }
};

/**
 * Count a marker's marked scripts per cohort and homework type, worked out
 * from the submissions themselves rather than the running counters above, so
 * it is never affected by resets. Returns [{ cohortId, cohortName, counts:
 * { [homeworkType]: n }, total, lastMarkedAt }], most recently marked first.
 */
export const getMarkerCountsByCohort = async (markerId, orgId) => {
  const resolvedOrgId = orgId || await getCurrentUserOrgId();
  const q = query(
    collection(db, 'submissions'),
    where('orgId', '==', resolvedOrgId),
    where('assignedTo', '==', markerId)
  );
  const snapshot = await getDocs(q);
  const byCohort = {};
  snapshot.forEach((d) => {
    const data = d.data();
    if (!data.markedAt) return; // claimed but not yet marked
    const key = data.cohortId || 'unknown';
    if (!byCohort[key]) {
      byCohort[key] = { cohortId: key, cohortName: data.cohortName || '', counts: {}, total: 0, lastMarkedAt: 0 };
    }
    const row = byCohort[key];
    row.counts[data.homeworkType] = (row.counts[data.homeworkType] || 0) + 1;
    row.total++;
    const markedMs = data.markedAt.toMillis ? data.markedAt.toMillis() : 0;
    if (markedMs > row.lastMarkedAt) row.lastMarkedAt = markedMs;
  });
  return Object.values(byCohort).sort((a, b) => b.lastMarkedAt - a.lastMarkedAt);
};

/**
 * Get all head marker view stats in the caller's own org
 */
export const getAllHeadMarkerViewStats = async (orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const q = query(collection(db, 'headMarkerStats'), where('orgId', '==', resolvedOrgId));
    const querySnapshot = await getDocs(q);
    const stats = {};

    querySnapshot.forEach((doc) => {
      stats[doc.id] = doc.data();
    });

    return stats;
  } catch (error) {
    console.error('Get all head marker view stats error:', error);
    return {};
  }
};

/**
 * Increment a marker's count for a specific homework type
 * Called automatically when marking is completed
 * Tracks in BOTH marker's view and head marker's independent view
 */
export const incrementMarkerStats = async (markerId, homeworkType, orgId) => {
  try {
    if (!markerId || !homeworkType) {
      console.warn('incrementMarkerStats called with missing markerId or homeworkType');
      return;
    }
    const resolvedOrgId = orgId || await getCurrentUserOrgId();

    // Increment marker's own stats (resettable by marker)
    const markerDocRef = doc(db, 'markerStats', markerId);
    const markerDocSnap = await getDoc(markerDocRef);

    if (!markerDocSnap.exists()) {
      await setDoc(markerDocRef, {
        orgId: resolvedOrgId,
        TH1: homeworkType === 'TH1' ? 1 : 0,
        TH2: homeworkType === 'TH2' ? 1 : 0,
        TH3: homeworkType === 'TH3' ? 1 : 0,
        TH4: homeworkType === 'TH4' ? 1 : 0,
        RH1: homeworkType === 'RH1' ? 1 : 0,
        RH2: homeworkType === 'RH2' ? 1 : 0,
        RH3: homeworkType === 'RH3' ? 1 : 0,
        RH4: homeworkType === 'RH4' ? 1 : 0,
        lastReset: null,
        lastUpdated: serverTimestamp()
      });
    } else {
      await updateDoc(markerDocRef, {
        orgId: resolvedOrgId,
        [homeworkType]: increment(1),
        lastUpdated: serverTimestamp()
      });
    }

    // ALSO increment head marker's independent view (never reset by markers)
    const headMarkerDocRef = doc(db, 'headMarkerStats', markerId);
    const headMarkerDocSnap = await getDoc(headMarkerDocRef);

    if (!headMarkerDocSnap.exists()) {
      await setDoc(headMarkerDocRef, {
        orgId: resolvedOrgId,
        TH1: homeworkType === 'TH1' ? 1 : 0,
        TH2: homeworkType === 'TH2' ? 1 : 0,
        TH3: homeworkType === 'TH3' ? 1 : 0,
        TH4: homeworkType === 'TH4' ? 1 : 0,
        RH1: homeworkType === 'RH1' ? 1 : 0,
        RH2: homeworkType === 'RH2' ? 1 : 0,
        RH3: homeworkType === 'RH3' ? 1 : 0,
        RH4: homeworkType === 'RH4' ? 1 : 0,
        lastReset: null,
        lastUpdated: serverTimestamp()
      });
    } else {
      await updateDoc(headMarkerDocRef, {
        orgId: resolvedOrgId,
        [homeworkType]: increment(1),
        lastUpdated: serverTimestamp()
      });
    }

    console.log('Marker stats incremented (both views):', markerId, homeworkType);
  } catch (error) {
    console.error('Increment marker stats error:', error);
    // Don't throw - this is non-critical
  }
};

/**
 * Reset statistics for a specific marker (marker's own view only)
 * Logs the reset in the permanent audit log
 */
export const resetMarkerStats = async (markerId, orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const docRef = doc(db, 'markerStats', markerId);
    const docSnap = await getDoc(docRef);

    // Save current stats to permanent audit log before resetting
    if (docSnap.exists()) {
      const currentStats = docSnap.data();

      // Create audit log entry
      const auditRef = doc(collection(db, 'resetHistory', markerId, 'resets'));
      await setDoc(auditRef, {
        ...homeworkCounts(currentStats),
        resetAt: serverTimestamp(),
        resetBy: 'marker',
        resetByUserId: markerId,
        type: 'marker_reset'
      });
    }

    // Reset marker's own stats
    await setDoc(docRef, {
      orgId: resolvedOrgId,
      ...zeroCounts(docSnap.exists() ? docSnap.data() : null),
      lastReset: serverTimestamp(),
      lastUpdated: serverTimestamp()
    });

    console.log('Marker stats reset (marker view only):', markerId);
  } catch (error) {
    console.error('Reset marker stats error:', error);
    throw error;
  }
};

/**
 * Reset head marker's independent view of marker stats
 * Does NOT affect the marker's own view
 * Logs the reset in the permanent audit log
 */
export const resetHeadMarkerViewStats = async (markerId, resetByUserId, orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const docRef = doc(db, 'headMarkerStats', markerId);
    const docSnap = await getDoc(docRef);

    // Save current stats to permanent audit log before resetting
    if (docSnap.exists()) {
      const currentStats = docSnap.data();

      // Create audit log entry
      const auditRef = doc(collection(db, 'resetHistory', markerId, 'resets'));
      await setDoc(auditRef, {
        ...homeworkCounts(currentStats),
        resetAt: serverTimestamp(),
        resetBy: 'head_marker',
        resetByUserId: resetByUserId,
        type: 'head_marker_reset'
      });
    }

    // Reset head marker's view
    await setDoc(docRef, {
      orgId: resolvedOrgId,
      ...zeroCounts(docSnap.exists() ? docSnap.data() : null),
      lastReset: serverTimestamp(),
      lastUpdated: serverTimestamp()
    });

    console.log('Head marker view reset for marker:', markerId);
  } catch (error) {
    console.error('Reset head marker view stats error:', error);
    throw error;
  }
};

/**
 * Reset statistics for all markers
 */
export const resetAllMarkerStats = async (orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const q = query(collection(db, 'markerStats'), where('orgId', '==', resolvedOrgId));
    const querySnapshot = await getDocs(q);

    const resetPromises = [];
    querySnapshot.forEach((docSnap) => {
      resetPromises.push(resetMarkerStats(docSnap.id, resolvedOrgId));
    });

    await Promise.all(resetPromises);

    console.log('All marker stats reset');
  } catch (error) {
    console.error('Reset all marker stats error:', error);
    throw error;
  }
};

/**
 * Get total count for a marker across all homework types
 */
export const getMarkerTotalCount = async (markerId) => {
  try {
    const stats = await getMarkerStats(markerId);
    
    const total = Object.values(homeworkCounts(stats)).reduce((sum, count) => sum + count, 0);
    
    return total;
  } catch (error) {
    console.error('Get marker total count error:', error);
    return 0;
  }
};

/**
 * Get a marker's own reset history (their "marker_reset" entries only —
 * excludes head marker resets, which markers should not see)
 */
export const getMarkerOwnResetHistory = async (markerId) => {
  try {
    const fullLog = await getMarkerResetAuditLog(markerId);
    return fullLog.filter(entry => entry.type === 'marker_reset');
  } catch (error) {
    console.error('Get marker own reset history error:', error);
    return [];
  }
};

/**
 * Delete a single reset log entry (used to prune old entries as the
 * audit log grows). Firestore rules restrict who can delete which entries.
 */
export const deleteResetLogEntry = async (markerId, resetId) => {
  try {
    await deleteDoc(doc(db, 'resetHistory', markerId, 'resets', resetId));
  } catch (error) {
    console.error('Delete reset log entry error:', error);
    throw error;
  }
};

/**
 * Get comprehensive permanent audit log for a marker
 * Shows all resets (both marker and head marker) with full details
 */
export const getMarkerResetAuditLog = async (markerId) => {
  try {
    const historySnapshot = await getDocs(
      collection(db, 'resetHistory', markerId, 'resets')
    );
    
    const history = [];
    historySnapshot.forEach((doc) => {
      history.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by resetAt timestamp (most recent first)
    history.sort((a, b) => {
      if (!a.resetAt || !b.resetAt) return 0;
      return b.resetAt.toMillis() - a.resetAt.toMillis();
    });
    
    return history;
  } catch (error) {
    console.error('Get marker reset audit log error:', error);
    return [];
  }
};